import { FC, useState } from "react";
import { toast } from "react-toastify";

interface FilePreviewProps {
  preview: {
    url: string;
    name: string;
    id?: string;
    type: string;
    isLoading?: boolean;
  };
  index: number;
  isUploading: boolean;
  onRemove: (index: number) => void;
  readOnly?: boolean;
  infoContent?: React.ReactNode;
}

const FilePreview: FC<FilePreviewProps> = ({
  preview,
  index,
  isUploading,
  onRemove,
  readOnly = false,
  infoContent,
}) => {
  const [showFullImage, setShowFullImage] = useState(false);

  const isImageFile = (type: string): boolean => type.startsWith("image/");

  const getFileIcon = (type: string): JSX.Element => {
    if (type.includes("pdf")) {
      return (
        <svg
          className="w-12 h-12 text-red-500"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" />
          <path d="M14 2v6h6M9 13h6M9 17h6M9 9h1" />
        </svg>
      );
    }
    if (type.includes("word") || type.includes("document")) {
      return (
        <svg
          className="w-12 h-12 text-blue-600"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" />
          <path d="M14 2v6h6M10 9l-2 6h1l1-3 1 3h1l-2-6z" />
        </svg>
      );
    }
    if (type.includes("sheet") || type.includes("excel")) {
      return (
        <svg
          className="w-12 h-12 text-green-600"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" />
          <path d="M14 2v6h6M10 12h4M10 15h4M10 18h4" />
        </svg>
      );
    }
    return (
      <svg
        className="w-12 h-12 text-gray-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
        />
      </svg>
    );
  };

  const getFileExtension = (fileName: string): string => {
    const parts = fileName.split(".");
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : "FILE";
  };

  const handleFileClick = async (action: "open" | "download" = "open") => {
    if (isImageFile(preview.type)) {
      setShowFullImage(true);
      return;
    }

    if (!preview.id) {
      toast.warning("فایل هنوز آپلود نشده است");
      return;
    }

    try {
      const fileUrl = `${process.env.REACT_APP_BASE_URL}/storage-api/indexed-objects/${preview.id}/as-file`;

      if (action === "open") {
        window.open(fileUrl, "_blank");
      } else {
        const response = await fetch(fileUrl);
        if (!response.ok) throw new Error("خطا در دریافت فایل");

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = preview.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success("فایل با موفقیت دانلود شد");
      }
    } catch (error) {
      console.error("Error handling file:", error);
      toast.error(`خطا در ${action === "open" ? "باز کردن" : "دانلود"} فایل`);
    }
  };
  console.log(infoContent)
  return (
    <>
      {/* Wrapper جدید برای قرار دادن باکس فایل و اطلاعات اضافی زیر هم */}
      <div className="flex flex-col h-full w-full">
        <div className="relative group rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-400 transition-all flex-grow">
          {/* نمایش محتوا */}
          {preview.isLoading ? (
            <div className="w-full h-32 bg-gray-100 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : preview.url === "document" ? (
            <div
              onClick={() => handleFileClick("open")}
              onContextMenu={(e) => {
                e.preventDefault();
                handleFileClick("download");
              }}
              className="w-full h-32 bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col items-center justify-center cursor-pointer hover:from-blue-50 hover:to-blue-100 transition-all duration-200 group/file"
              title="کلیک چپ: باز کردن | کلیک راست: دانلود"
            >
              <div className="transform group-hover/file:scale-110 transition-transform">
                {getFileIcon(preview.type)}
              </div>
              <span className="mt-2 px-2 py-0.5 bg-white rounded text-xs font-semibold text-gray-700 shadow-sm">
                {getFileExtension(preview.name)}
              </span>
              <span className="text-xs text-blue-600 mt-2 opacity-0 group-hover/file:opacity-100 transition-opacity">
                کلیک برای باز کردن
              </span>
            </div>
          ) : (
            <div
              onClick={() => isImageFile(preview.type) && setShowFullImage(true)}
              className={`w-full h-32 ${
                isImageFile(preview.type) ? "cursor-pointer" : ""
              }`}
            >
              <img
                src={preview.url}
                alt={`پیش‌نمایش ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* اورلی در حال آپلود */}
          {isUploading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20">
              <div className="text-white text-sm flex flex-col items-center">
                <svg
                  className="animate-spin h-6 w-6 mb-1"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>در حال آپلود...</span>
              </div>
            </div>
          )}

          {/* دکمه حذف - فقط در حالت غیر ReadOnly */}
          {!readOnly && !isUploading && !preview.isLoading && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(index);
              }}
              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 z-30 shadow-lg"
              title="حذف فایل"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}

          {/* نام فایل */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
            <p className="text-xs text-white truncate" title={preview.name}>
              {preview.name}
            </p>
          </div>
        </div>

        {/* محتوای اطلاعات اضافی زیر عکس */}
        {infoContent && (
          <div className="mt-2 w-full text-xs text-gray-700">
            {infoContent}
          </div>
        )}
      </div>

      {/* مودال نمایش تصویر کامل */}
      {showFullImage && isImageFile(preview.type) && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowFullImage(false)}
        >
          <button
            onClick={() => setShowFullImage(false)}
            className="absolute top-4 right-4 text-white bg-red-500 rounded-full p-2 hover:bg-red-600 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          <img
            src={preview.url}
            alt={preview.name}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default FilePreview;
